import React from "react";
import { FileText, Link2, Upload, Video, X } from "lucide-react";
import { AppButton, IconButton } from "../ui";

/**
 * What hangs on a syllabus day or one of its topics — recordings (the batch's
 * Videos) and materials — and the three ways to add to it.
 *
 * The actions sit on the line they belong to (a topic's name, the day's
 * heading) and the chips share one wrapping line under it, shown only when
 * there is something to show — a day of many topics stays short.
 */

const RECORDING_STATUS = { processing: "processing", failed: "failed" };

/**
 * Map recording · Upload · Link existing, on one row.
 *
 * Props:
 *   target — "“Variables”" / "the whole day", completing each button's name
 *   onMapRecording, onUpload, onLink
 */
export function AttachmentActions({ target, onMapRecording, onUpload, onLink }) {
  return (
    <span className="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-0.5">
      <AppButton variant="ghost" size="xs" onClick={onMapRecording} aria-label={`Map recording to ${target}`}>
        <Video size={13} aria-hidden="true" /> Map recording
      </AppButton>
      <AppButton variant="ghost" size="xs" onClick={onUpload} aria-label={`Upload to ${target}`}>
        <Upload size={13} aria-hidden="true" /> Upload
      </AppButton>
      <AppButton variant="ghost" size="xs" onClick={onLink} aria-label={`Link existing to ${target}`}>
        <Link2 size={13} aria-hidden="true" /> Link existing
      </AppButton>
    </span>
  );
}

function Chip({ icon: Icon, children }) {
  return (
    <span className="tw-inline-flex tw-max-w-full tw-items-center tw-gap-0.5 tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-pl-1.5">
      <Icon size={13} aria-hidden="true" className="tw-shrink-0 tw-text-content-muted" />
      {children}
    </span>
  );
}

/**
 * The recordings and materials of one day or topic, as chips on one line.
 * Renders nothing when there are none.
 *
 * Props:
 *   recordings — as GET /syllabus/batch/:id/syllabus describes them
 *                ({ videoId, title, status } or { legacy, recordingId })
 *   materials, materialsLoading
 *   where      — "this day" / "this topic", for the remove buttons' names
 *   onOpenRecording, onRemoveRecording, onOpenMaterial, onRemoveMaterial
 *   removingId — the recording or material being removed right now
 *   className  — placement (indent) from the caller
 *
 * A course-level material reaches every batch of the course, so it opens but
 * cannot be taken off one batch's syllabus.
 */
export function AttachmentChips({
  recordings = [], materials = [], materialsLoading = false, where,
  onOpenRecording, onRemoveRecording, onOpenMaterial, onRemoveMaterial, removingId, className = "",
}) {
  if (!recordings.length && !materials.length && !materialsLoading) return null;
  return (
    <div className={`tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 ${className}`}>
      {recordings.map((rec) => {
        const ref = rec.videoId || rec.recordingId;
        const title = rec.legacy ? "Earlier recording" : rec.title || "Untitled recording";
        return (
          <Chip key={`r-${ref}`} icon={Video}>
            {rec.videoId ? (
              <AppButton variant="ghost" size="xs" onClick={() => onOpenRecording?.(rec)} title={`Watch ${title}`}>
                <span className="tw-max-w-[14rem] tw-truncate">{title}</span>
              </AppButton>
            ) : (
              <span className="tw-px-1.5 tw-text-xs tw-text-content-muted">{title}</span>
            )}
            {RECORDING_STATUS[rec.status] && (
              <span className="tw-pr-1 tw-text-xs tw-text-warning-fg">{RECORDING_STATUS[rec.status]}</span>
            )}
            {onRemoveRecording && ref && (
              <IconButton
                label={`Remove “${title}” from ${where}`}
                icon={X}
                size="xs"
                onClick={() => onRemoveRecording(rec)}
                disabled={removingId === ref}
              />
            )}
          </Chip>
        );
      })}
      {materials.map((m) => {
        const fromCourse = m.level === "course" || !m.batch;
        return (
          <Chip key={`m-${m._id}`} icon={FileText}>
            <AppButton variant="ghost" size="xs" onClick={() => onOpenMaterial?.(m)} title={`Open ${m.title}`}>
              <span className="tw-max-w-[14rem] tw-truncate">{m.title}</span>
            </AppButton>
            {fromCourse && <span className="tw-pr-1.5 tw-text-xs tw-text-content-subtle">Course</span>}
            {!fromCourse && onRemoveMaterial && (
              <IconButton
                label={`Remove “${m.title}” from ${where}`}
                icon={X}
                size="xs"
                onClick={() => onRemoveMaterial(m)}
                disabled={removingId === m._id}
              />
            )}
          </Chip>
        );
      })}
      {materialsLoading && !materials.length && (
        <span className="tw-text-xs tw-text-content-subtle">Loading materials…</span>
      )}
    </div>
  );
}
